import React from 'react';
import { makeObservable, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import type { IModalDialogContent, ModalDialogOptions, ModalWindow } from '@components/Modal.tsx';
import { ModalButtonType } from '@components/Modal.tsx';
import apiService from '@services/apiService.ts';
import { fileService } from '@services/fileService.ts';
import { toastService } from '@components/Toast.tsx';
import { Timestamp } from '@components/Timestamp.tsx';
import { ImgUp } from '@components/Images/ImgUp.tsx';
import { ImgDownload } from '@components/Images/ImgDownload.tsx';

export type StorageOverviewData = {
  userId: string;
  displayName: string;
};

type TopFile = { id: string; fileName: string; fileSize: number; dateTime: string; lastRequest: string | null };
type Overview = { topFiles: TopFile[]; totalUsedSpace: number; totalFiles: number };

const SLICE_COLORS = [
  '#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f',
  '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
];

function PieChart({ data }: { data: { label: string; value: number; color: string }[] }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;

  const cx = 100, cy = 100, r = 90;
  const slices: React.ReactElement[] = [];
  let startAngle = -Math.PI / 2;

  for (let i = 0; i < data.length; i++) {
    const slice = data[i];
    const angle = (slice.value / total) * 2 * Math.PI;
    const endAngle = startAngle + angle;
    const x1 = cx + r * Math.cos(startAngle);
    const y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(endAngle);
    const y2 = cy + r * Math.sin(endAngle);
    const largeArc = angle > Math.PI ? 1 : 0;
    const d = `M ${cx} ${cy} L ${x1.toFixed(2)} ${y1.toFixed(2)} A ${r} ${r} 0 ${largeArc} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`;
    slices.push(<path key={i} d={d} fill={slice.color} />);
    startAngle = endAngle;
  }

  return (
    <svg viewBox="0 0 200 200" style={{ width: 200, height: 200, display: 'block' }}>
      {slices}
    </svg>
  );
}

@observer
export class StorageOverviewDialog extends React.Component<StorageOverviewData> implements IModalDialogContent<void> {
  @observable private _userId: string;
  @observable private _displayName: string;
  @observable private _data: Overview | null = null;
  @observable private _loading = true;
  @observable private _error: string | null = null;

  constructor(props: StorageOverviewData) {
    super(props);
    this._userId = props.userId;
    this._displayName = props.displayName;
    makeObservable(this);
  }

  async componentDidMount() {
    try {
      const data = await apiService.get<Overview>(`/api/user/${this._userId}/storage-overview`);
      runInAction(() => {
        this._data = data;
        this._loading = false;
      });
    } catch (e) {
      const msg = 'Failed to load storage data';
      toastService.error(msg, 'Storage Overview');
      runInAction(() => {
        this._error = msg;
        this._loading = false;
      });
    }
  }

  getModalOptions(window: ModalWindow<void>): ModalDialogOptions<void> {
    return {
      title: `Storage Overview — ${this._displayName}`,
      width: '640px',
      closeByEscape: true,
      closeOnClickOutside: true,
      buttons: [
        {
          title: 'Close',
          type: ModalButtonType.Cancel,
          onClick: () => window.close(ModalButtonType.Cancel),
        },
      ],
    };
  }

  render() {
    if (this._loading) {
      return <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-secondary)' }}>Loading…</div>;
    }
    if (this._error) {
      return <div style={{ padding: '24px', color: 'var(--danger)' }}>{this._error}</div>;
    }
    if (!this._data) return null;

    const { topFiles, totalUsedSpace, totalFiles } = this._data;
    const topUsed = topFiles.reduce((s, f) => s + f.fileSize, 0);
    const otherUsed = Math.max(0, totalUsedSpace - topUsed);

    const sliceData = [
      ...topFiles.map((f, i) => ({ label: f.fileName, value: f.fileSize, color: SLICE_COLORS[i % SLICE_COLORS.length] })),
      ...(otherUsed > 0 ? [{ label: 'Other', value: otherUsed, color: '#cccccc' }] : []),
    ];

    return (
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        <div style={{ flexShrink: 0 }}>
          <PieChart data={sliceData} />
          <div style={{ textAlign: 'center', marginTop: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
            {fileService.formatFileSize(totalUsedSpace)} total · {totalFiles} files
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>#</th>
                <th style={{ textAlign: 'left', padding: '4px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>File</th>
                <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>Size</th>
                <th style={{ textAlign: 'right', padding: '4px 8px', color: 'var(--text-secondary)', fontWeight: 500 }}>%</th>
              </tr>
            </thead>
            <tbody>
              {topFiles.map((f, i) => (
                <tr key={f.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '6px 8px' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: SLICE_COLORS[i % SLICE_COLORS.length], marginRight: 6, verticalAlign: 'middle' }} />
                    {i + 1}
                  </td>
                  <td style={{ padding: '6px 8px', overflow: 'hidden', maxWidth: 220 }}>
                    <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.fileName}>
                      {f.fileName}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'nowrap' }}>
                      {f.dateTime && !isNaN(new Date(f.dateTime).getTime())
                        ? <><span><ImgUp /> <Timestamp timestamp={f.dateTime} short /></span></>
                        : null}
                      {f.lastRequest && !isNaN(new Date(f.lastRequest).getTime())
                        ? <><span style={{ marginLeft: 10 }}><ImgDownload size={14} /> <Timestamp timestamp={f.lastRequest} short /></span></>
                        : <span style={{ marginLeft: 10, fontStyle: 'italic' }}>never accessed</span>}
                    </div>
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {fileService.formatFileSize(f.fileSize)}
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    {totalUsedSpace > 0 ? Math.round((f.fileSize / totalUsedSpace) * 100) : 0}%
                  </td>
                </tr>
              ))}
              {otherUsed > 0 && (
                <tr>
                  <td style={{ padding: '6px 8px' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#cccccc', marginRight: 6, verticalAlign: 'middle' }} />
                    —
                  </td>
                  <td style={{ padding: '6px 8px', color: 'var(--text-secondary)' }}>
                    Other ({totalFiles - topFiles.length} files)
                  </td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fileService.formatFileSize(otherUsed)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>
                    {totalUsedSpace > 0 ? Math.round((otherUsed / totalUsedSpace) * 100) : 0}%
                  </td>
                </tr>
              )}
              {topFiles.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '16px 8px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                    No files found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
}
